import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * useQuotaGate - Hook for quota checking with upgrade modal integration
 * Returns modal state and functions to check/use quota with automatic modal trigger
 */
export function useQuotaGate() {
  const [modalState, setModalState] = useState({
    isOpen: false,
    quotaType: null,
    used: 0,
    limit: 0,
    planName: '',
    role: ''
  });
  const [loading, setLoading] = useState(false);

  const openModal = (quotaType, used, limit, planName, role) => {
    setModalState({
      isOpen: true,
      quotaType,
      used,
      limit,
      planName,
      role
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  /**
   * Check quota before performing an action
   * Returns true if action is allowed, false otherwise
   * Automatically opens upgrade modal if quota exceeded
   */
  const checkAndGate = async (quotaType, role) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quota/check/${quotaType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { has_remaining, used, limit, plan_name } = response.data;
      
      if (!has_remaining) {
        openModal(quotaType, used, limit, plan_name, role);
        return false;
      }
      
      return true;
    } catch (err) {
      return true; // Fail open
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check and consume quota for an action
   * Returns true if action was allowed and quota consumed
   * Automatically opens upgrade modal if quota exceeded
   */
  const consumeAndGate = async (quotaType, role) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/quota/check-and-use/${quotaType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (err) {
      if (err.response?.status === 403) {
        const detail = err.response.data.detail || {};
        openModal(
          quotaType, 
          detail.used || 0, 
          detail.limit || 0, 
          detail.plan_name || 'Free',
          role
        );
        return false;
      }
      return true; // Fail open
    } finally {
      setLoading(false);
    }
  };

  return {
    modalState,
    closeModal,
    checkAndGate,
    consumeAndGate,
    loading
  };
}

export default useQuotaGate;
